/**
 * Utilitaires de groupement pour lieux et objets
 */

export interface GroupedItems<T> {
  key: string;
  label: string;
  items: T[];
  count: number;
}

/**
 * Groupement simple par un critère
 */
export const groupItems = <T extends Record<string, any>>(
  items: T[],
  groupBy: string
): GroupedItems<T>[] => {
  if (!groupBy || groupBy === 'none') {
    return [
      {
        key: 'all',
        label: 'Tous les éléments',
        items,
        count: items.length,
      },
    ];
  }

  const groups: Record<string, T[]> = {};

  items.forEach((item) => {
    let groupKey = '';
    let groupLabel = '';

    switch (groupBy) {
      // Groupement Lieux
      case 'site':
        groupKey = item.piece?.batiment?.site?.id || 'sans-site';
        groupLabel = item.piece?.batiment?.site?.nom || 'Sans site';
        break;

      case 'batiment':
        groupKey = item.piece?.batiment?.id || 'sans-batiment';
        groupLabel = item.piece?.batiment?.nom || 'Sans bâtiment';
        break;

      case 'piece':
        groupKey = item.piece?.id || 'sans-piece';
        groupLabel = item.piece?.nom || 'Sans pièce';
        break;

      case 'theme':
        // Un item peut avoir plusieurs thèmes (séparés par virgule)
        const themes = item.theme?.split(',').map((t: string) => t.trim()) || ['Sans thème'];
        themes.forEach((theme: string) => {
          const themeKey = theme || 'sans-theme';
          if (!groups[themeKey]) groups[themeKey] = [];
          // Éviter les doublons si déjà ajouté
          if (!groups[themeKey].find((i) => i.id === item.id)) {
            groups[themeKey].push(item);
          }
        });
        return Object.entries(groups)
          .map(([key, items]) => ({
            key,
            label: key,
            items,
            count: items.length,
          }))
          .sort((a, b) => a.label.localeCompare(b.label));

      // Groupement spécifique aux Sites
      case 'trigram':
        groupKey = item.trigram || 'sans-trigramme';
        groupLabel = item.trigram || 'Sans trigramme';
        break;

      case 'batiments':
        const batimentCount = item.batiments?.length || 0;
        groupKey = `batiments-${batimentCount}`;
        groupLabel = `${batimentCount} bâtiment${batimentCount > 1 ? 's' : ''}`;
        break;

      // Groupement spécifique aux Bâtiments
      case 'etages':
        const etageCount = item.nombreEtages || 0;
        groupKey = `etages-${etageCount}`;
        groupLabel = `${etageCount} étage${etageCount > 1 ? 's' : ''}`;
        break;

      case 'pieces':
        const pieceCount = item.pieces?.length || 0;
        groupKey = `pieces-${pieceCount}`;
        groupLabel = `${pieceCount} pièce${pieceCount > 1 ? 's' : ''}`;
        break;

      // Groupement spécifique aux Pièces
      case 'etage':
        const etage = item.etage || 0;
        groupKey = `etage-${etage}`;
        groupLabel = `Étage ${etage}`;
        break;

      case 'objets':
        const objetCount = item.objets?.length || 0;
        groupKey = `objets-${objetCount}`;
        groupLabel = `${objetCount} objet${objetCount > 1 ? 's' : ''}`;
        break;

      // Groupement Objets
      case 'categorie':
        groupKey = item.categorie || 'sans-categorie';
        groupLabel = item.categorie || 'Sans catégorie';
        break;

      case 'sous-categorie':
        groupKey = item.sousCategorie || 'sans-sous-categorie';
        groupLabel = item.sousCategorie || 'Sans sous-catégorie';
        break;

      case 'etat':
        groupKey = item.etat || 'sans-etat';
        groupLabel = formatEtat(item.etat) || 'Sans état';
        break;

      case 'lieu':
        if (item.box) {
          groupKey = `box-${item.box.id}`;
          groupLabel = `📦 ${item.box.nom} (${item.box.code})`;
        } else if (item.zone) {
          groupKey = `zone-${item.zone.id}`;
          groupLabel = `🏠 ${item.zone.nom} (${item.zone.code})`;
        } else {
          groupKey = 'sans-lieu';
          groupLabel = 'Sans emplacement';
        }
        break;

      case 'couleur':
        groupKey = item.couleurPrincipale || 'sans-couleur';
        groupLabel = item.couleurPrincipale || 'Sans couleur';
        break;

      default:
        groupKey = 'default';
        groupLabel = 'Autres';
    }

    if (!groups[groupKey]) groups[groupKey] = [];
    groups[groupKey].push(item);
  });

  return Object.entries(groups)
    .map(([key, items]) => {
      let label = key;
      
      // Déterminer le label selon le type de groupement
      switch (groupBy) {
        case 'site':
          label = items[0]?.piece?.batiment?.site?.nom || 'Sans site';
          break;
        case 'batiment':
          label = items[0]?.piece?.batiment?.nom || 'Sans bâtiment';
          break;
        case 'piece':
          label = items[0]?.piece?.nom || 'Sans pièce';
          break;
        case 'theme':
          label = key;
          break;
        
        // Groupement spécifique aux Sites
        case 'trigram':
          label = items[0]?.trigram || 'Sans trigramme';
          break;
        case 'batiments':
          const batimentCount = items[0]?.batiments?.length || 0;
          label = `${batimentCount} bâtiment${batimentCount > 1 ? 's' : ''}`;
          break;
        
        // Groupement spécifique aux Bâtiments
        case 'etages':
          const etageCount = items[0]?.nombreEtages || 0;
          label = `${etageCount} étage${etageCount > 1 ? 's' : ''}`;
          break;
        case 'pieces':
          const pieceCount = items[0]?.pieces?.length || 0;
          label = `${pieceCount} pièce${pieceCount > 1 ? 's' : ''}`;
          break;
        
        // Groupement spécifique aux Pièces
        case 'etage':
          const etage = items[0]?.etage || 0;
          label = `Étage ${etage}`;
          break;
        case 'objets':
          const objetCount = items[0]?.objets?.length || 0;
          label = `${objetCount} objet${objetCount > 1 ? 's' : ''}`;
          break;
        
        // Groupement Objets
        case 'categorie':
          label = items[0]?.categorie || 'Sans catégorie';
          break;
        case 'sous-categorie':
          label = items[0]?.sousCategorie || 'Sans sous-catégorie';
          break;
        case 'etat':
          label = formatEtat(items[0]?.etat) || 'Sans état';
          break;
        case 'lieu':
          if (items[0]?.box) {
            label = `📦 ${items[0].box.nom} (${items[0].box.code})`;
          } else if (items[0]?.zone) {
            label = `🏠 ${items[0].zone.nom} (${items[0].zone.code})`;
          } else {
            label = 'Sans emplacement';
          }
          break;
        case 'couleur':
          label = items[0]?.couleurPrincipale || 'Sans couleur';
          break;
        default:
          label = key;
      }
      
      return {
        key,
        label,
        items,
        count: items.length,
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));
};

/**
 * Groupement mixte (deux critères)
 */
export const groupItemsMultiple = <T extends Record<string, any>>(
  items: T[],
  groupBy: string
): { primary: string; secondary: string; groups: GroupedItems<GroupedItems<T>>[] } => {
  const [primary, secondary] = groupBy.split('-');

  if (!secondary) {
    // Pas de critère secondaire, utiliser groupement simple
    const simpleGroups = groupItems(items, primary);
    return {
      primary,
      secondary: 'none',
      groups: simpleGroups.map((group) => ({
        ...group,
        items: [
          {
            key: 'all',
            label: 'Tous',
            items: group.items,
            count: group.items.length,
          },
        ],
      })) as any,
    };
  }

  // Grouper par critère primaire
  const primaryGroups = groupItems(items, primary);

  // Pour chaque groupe primaire, grouper par critère secondaire
  const nestedGroups = primaryGroups.map((primaryGroup) => ({
    key: primaryGroup.key,
    label: primaryGroup.label,
    items: groupItems(primaryGroup.items, secondary),
    count: primaryGroup.count,
  }));

  return {
    primary,
    secondary,
    groups: nestedGroups,
  };
};

/**
 * Formatage des états pour affichage
 */
const formatEtat = (etat: string): string => {
  const etats: Record<string, string> = {
    bon: 'Bon',
    'à réparer': 'À réparer',
    incomplet: 'Incomplet',
    neuf: 'Neuf',
    usé: 'Usé',
    hs: 'HS',
  };
  return etats[etat] || etat;
};

/**
 * Obtenir le label d'un groupe
 */
export const getGroupLabel = (groupKey: string, groupBy: string): string => {
  // Labels par défaut pour les éléments sans valeur
  if (groupKey === 'sans-site') return '📍 Sans site';
  if (groupKey === 'sans-batiment') return '🏗️ Sans bâtiment';
  if (groupKey === 'sans-piece') return '🚪 Sans pièce';
  if (groupKey === 'sans-theme') return '🎨 Sans thème';
  if (groupKey === 'sans-categorie') return '📁 Sans catégorie';
  if (groupKey === 'sans-sous-categorie') return '📂 Sans sous-catégorie';
  if (groupKey === 'sans-etat') return '⚙️ Sans état';
  if (groupKey === 'sans-lieu') return '📍 Sans emplacement';
  if (groupKey === 'sans-couleur') return '🎨 Sans couleur';
  if (groupKey === 'sans-trigramme') return '🔤 Sans trigramme';

  // Labels spécifiques pour les groupements par nombre
  if (groupBy === 'batiments' && groupKey.startsWith('batiments-')) {
    const count = groupKey.replace('batiments-', '');
    return `${count} bâtiment${count !== '1' ? 's' : ''}`;
  }
  if (groupBy === 'etages' && groupKey.startsWith('etages-')) {
    const count = groupKey.replace('etages-', '');
    return `${count} étage${count !== '1' ? 's' : ''}`;
  }
  if (groupBy === 'pieces' && groupKey.startsWith('pieces-')) {
    const count = groupKey.replace('pieces-', '');
    return `${count} pièce${count !== '1' ? 's' : ''}`;
  }
  if (groupBy === 'objets' && groupKey.startsWith('objets-')) {
    const count = groupKey.replace('objets-', '');
    return `${count} objet${count !== '1' ? 's' : ''}`;
  }
  if (groupBy === 'etage' && groupKey.startsWith('etage-')) {
    const etage = groupKey.replace('etage-', '');
    return `Étage ${etage}`;
  }

  return groupKey;
};

